import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { ArrowLeft, ArrowRight, Loader2, Truck, PenLine, Check, ChevronLeft, Tag, X } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import StripePaymentForm from "@/components/StripePaymentForm";
import { api, BOOK } from "@/lib/book";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

// Brand-matched Stripe Elements appearance
const stripeAppearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#D92D20",
    colorBackground: "#FFFFFF",
    colorText: "#0A0A0A",
    colorDanger: "#D92D20",
    colorTextSecondary: "#525252",
    colorTextPlaceholder: "#A3A3A3",
    fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, sans-serif',
    fontSizeBase: "15px",
    fontWeightNormal: "400",
    fontWeightMedium: "500",
    fontWeightBold: "700",
    spacingUnit: "4px",
    borderRadius: "0px",
    focusBoxShadow: "0 0 0 1px #0A0A0A",
    focusOutline: "none",
  },
  rules: {
    ".Input": {
      border: "1px solid rgba(10,10,10,0.15)",
      padding: "12px",
      boxShadow: "none",
    },
    ".Input:focus": {
      border: "1px solid #0A0A0A",
      boxShadow: "none",
    },
    ".Label": {
      color: "#525252",
      fontSize: "10px",
      fontWeight: "700",
      letterSpacing: "0.25em",
      textTransform: "uppercase",
      marginBottom: "6px",
    },
    ".Tab": {
      border: "1px solid rgba(10,10,10,0.15)",
      boxShadow: "none",
      padding: "10px 14px",
    },
    ".Tab--selected": {
      border: "1px solid #0A0A0A",
      backgroundColor: "#0A0A0A",
      color: "#FFFFFF",
    },
    ".TabIcon": {
      fill: "currentColor",
    },
    ".Error": {
      color: "#D92D20",
    },
  },
};

const COUNTRIES = [
  ["US", "United States"], ["CA", "Canada"], ["GB", "United Kingdom"],
  ["AU", "Australia"], ["DE", "Germany"], ["FR", "France"], ["IT", "Italy"],
  ["ES", "Spain"], ["NL", "Netherlands"], ["SE", "Sweden"], ["NO", "Norway"],
  ["DK", "Denmark"], ["IE", "Ireland"], ["NZ", "New Zealand"], ["MX", "Mexico"],
  ["BR", "Brazil"], ["JP", "Japan"], ["SG", "Singapore"], ["IN", "India"],
  ["ZA", "South Africa"], ["AE", "United Arab Emirates"], ["CH", "Switzerland"],
];

export default function Checkout() {
  const [step, setStep] = useState(1); // 1: address, 2: shipping rate, 3: pay
  const [qty, setQty] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", street1: "", street2: "",
    city: "", state: "", zip: "", country: "US", phone: "",
  });
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [creatingIntent, setCreatingIntent] = useState(false);
  // Promo code state
  const [promoInput, setPromoInput] = useState("");
  const [promoApplying, setPromoApplying] = useState(false);
  const [applied, setApplied] = useState(null); // { code, discount_amount, discount_type, discount_value }
  const [promoError, setPromoError] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const bookTotalNum = BOOK.price * qty;
  const shipCostNum = selectedRate ? selectedRate.amount : 0;
  const subtotalNum = bookTotalNum + shipCostNum;
  const discountNum = applied ? Math.min(applied.discount_amount, subtotalNum) : 0;
  const totalNum = Math.max(0, subtotalNum - discountNum);

  const bookTotal = bookTotalNum.toFixed(2);
  const shipCost = shipCostNum.toFixed(2);
  const total = totalNum.toFixed(2);

  const fetchRates = async () => {
    const required = ["name", "email", "street1", "city", "zip", "country"];
    for (const r of required) {
      if (!form[r] || !form[r].trim()) {
        toast.error(`Please fill in ${r}`);
        return;
      }
    }
    setLoadingRates(true);
    try {
      const { data } = await api.post("/shipping/rates", { address: form, quantity: qty });
      setRates(data.rates || []);
      setSelectedRate((data.rates && data.rates[0]) || null);
      setStep(2);
    } catch (e) {
      toast.error("Could not fetch shipping rates. Please try again.");
      console.error(e);
    } finally {
      setLoadingRates(false);
    }
  };

  const goToPay = async () => {
    if (!selectedRate) { toast.error("Select a shipping method"); return; }
    setCreatingIntent(true);
    try {
      const { data } = await api.post("/checkout/payment-intent", {
        quantity: qty,
        origin_url: window.location.origin,
        address: form,
        shipping_rate: selectedRate,
      });
      if (data.client_secret) {
        setClientSecret(data.client_secret);
        setPaymentIntentId(data.payment_intent_id);
        setStep(3);
      } else {
        toast.error("Could not initialize payment");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Payment initialization failed";
      toast.error(msg);
      console.error(e);
    } finally {
      setCreatingIntent(false);
    }
  };

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    if (!paymentIntentId) { toast.error("Continue to payment first"); return; }
    setPromoApplying(true);
    setPromoError("");
    try {
      const { data } = await api.post(
        `/checkout/payment-intent/${paymentIntentId}/promo`,
        { code }
      );
      setApplied({
        code: data.code,
        discount_amount: data.discount_amount,
        discount_type: data.discount_type,
        discount_value: data.discount_value,
      });
      setPromoInput("");
      toast.success(`Promo applied: ${data.code}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Invalid promo code";
      setPromoError(msg);
      setApplied(null);
    } finally {
      setPromoApplying(false);
    }
  };

  const removePromo = async () => {
    if (!paymentIntentId) { setApplied(null); return; }
    try {
      await api.post(`/checkout/payment-intent/${paymentIntentId}/promo`, { code: null });
    } catch (e) {
      // best-effort — clear local state regardless
      console.warn(e);
    }
    setApplied(null);
    setPromoError("");
  };

  const elementsOptions = useMemo(() => ({
    clientSecret,
    appearance: stripeAppearance,
    loader: "auto",
  }), [clientSecret]);

  const stepLabels = ["Shipping address", "Shipping speed", "Payment"];

  return (
    <div className="min-h-screen">
      <Nav />
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-12 lg:py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A]" data-testid="checkout-back">
          <ArrowLeft className="w-4 h-4" /> Back to book
        </Link>

        <div className="mt-4 mb-10">
          <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-[#D92D20] mb-3">
            Checkout · Step {step} of 3
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[0.95]" data-testid="checkout-title">
            {step === 1 ? "Where should we ship?" : step === 2 ? "Pick a shipping speed." : "Complete your order."}
          </h1>

          {/* Step indicator */}
          <ol className="mt-8 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.2em] font-bold">
            {stepLabels.map((label, idx) => {
              const n = idx + 1;
              const isActive = n === step;
              const isDone = n < step;
              return (
                <li
                  key={label}
                  className={`px-3 py-2 border ${isActive ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : isDone ? "border-[#D92D20] text-[#D92D20]" : "border-black/15 text-[#525252]"}`}
                >
                  {isDone ? <Check className="w-3 h-3 inline-block mr-1" /> : <span className="mr-1 font-mono-plex">{n}.</span>}
                  {label}
                </li>
              );
            })}
          </ol>
        </div>

        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-7">
            {step === 1 && (
              <div className="bg-white border border-black/10 p-7" data-testid="address-form">
                <h2 className="font-display font-bold text-2xl tracking-tight mb-6">Shipping address</h2>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Full name" v={form.name} onChange={handle("name")} testid="field-name" full />
                  <Field label="Email" v={form.email} onChange={handle("email")} type="email" testid="field-email" full />
                  <Field label="Street address" v={form.street1} onChange={handle("street1")} testid="field-street1" full />
                  <Field label="Apt / Suite (optional)" v={form.street2} onChange={handle("street2")} testid="field-street2" full />
                  <Field label="City" v={form.city} onChange={handle("city")} testid="field-city" />
                  <Field label="State / Province" v={form.state} onChange={handle("state")} testid="field-state" />
                  <Field label="ZIP / Postal code" v={form.zip} onChange={handle("zip")} testid="field-zip" />
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">Country</label>
                    <select
                      value={form.country}
                      onChange={handle("country")}
                      className="mt-2 w-full h-12 px-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm"
                      data-testid="field-country"
                    >
                      {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                    </select>
                  </div>
                  <Field label="Phone (optional)" v={form.phone} onChange={handle("phone")} testid="field-phone" full />
                </div>

                <button
                  onClick={fetchRates}
                  disabled={loadingRates}
                  className="mt-8 inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-7 py-4 hover:bg-[#D92D20] transition-colors disabled:opacity-60"
                  data-testid="get-rates-btn"
                >
                  {loadingRates ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />}
                  Continue to Shipping
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="bg-white border border-black/10 p-7" data-testid="rates-list">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display font-bold text-2xl tracking-tight">Shipping method</h2>
                  <button onClick={() => setStep(1)} className="text-sm text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid="edit-address-btn">
                    <ChevronLeft className="w-4 h-4" /> Edit address
                  </button>
                </div>
                <div className="space-y-3">
                  {rates.map((r) => (
                    <label
                      key={r.object_id}
                      className={`flex items-center gap-4 border p-4 cursor-pointer transition-colors ${selectedRate?.object_id === r.object_id ? "border-[#0A0A0A] bg-[#F5F5F5]" : "border-black/10 hover:border-[#0A0A0A]"}`}
                      data-testid={`rate-option-${r.object_id}`}
                    >
                      <input
                        type="radio"
                        name="rate"
                        checked={selectedRate?.object_id === r.object_id}
                        onChange={() => setSelectedRate(r)}
                        className="accent-[#D92D20]"
                      />
                      <div className="flex-1">
                        <div className="font-medium">{r.provider} · {r.servicelevel}</div>
                        <div className="text-xs text-[#525252] mt-0.5">
                          {r.duration_terms || (r.estimated_days ? `Approx. ${r.estimated_days} days` : "Tracked delivery")}
                        </div>
                      </div>
                      <div className="font-mono-plex font-medium">${r.amount.toFixed(2)}</div>
                    </label>
                  ))}
                </div>

                <button
                  onClick={goToPay}
                  disabled={creatingIntent || !selectedRate}
                  className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-white px-8 py-4 text-base font-medium hover:bg-[#D92D20] transition-colors disabled:opacity-60"
                  data-testid="continue-to-payment-btn"
                >
                  {creatingIntent ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  Continue to Payment <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {step === 3 && clientSecret && stripePromise && (
              <div data-testid="payment-step">
                <div className="mb-4 flex items-center justify-between text-sm">
                  <div className="text-[#525252]">
                    Shipping to <span className="text-[#0A0A0A] font-medium">{form.name}, {form.city}</span>
                  </div>
                  <button onClick={() => setStep(2)} className="text-[#525252] hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid="edit-shipping-btn">
                    <ChevronLeft className="w-4 h-4" /> Edit
                  </button>
                </div>

                {/* Promo code block */}
                <div className="bg-white border border-black/10 p-6 sm:p-7 mb-5" data-testid="promo-section">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold mb-4">
                    <Tag className="w-3.5 h-3.5" /> Promo code
                  </div>
                  {applied ? (
                    <div className="flex items-center justify-between bg-[#0A0A0A] text-white px-4 py-3" data-testid="promo-applied">
                      <div className="flex items-center gap-3">
                        <Check className="w-4 h-4 text-[#D92D20]" />
                        <div>
                          <div className="font-medium text-sm font-mono-plex">{applied.code}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                            {applied.discount_type === "percent"
                              ? `${applied.discount_value}% off`
                              : `$${applied.discount_value.toFixed(2)} off`}
                            {" "}· −${applied.discount_amount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={removePromo}
                        className="text-[#A3A3A3] hover:text-white p-1"
                        aria-label="Remove promo"
                        data-testid="promo-remove-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoInput}
                          onChange={(e) => { setPromoInput(e.target.value); setPromoError(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
                          placeholder="Enter code"
                          className="flex-1 h-12 px-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm font-mono-plex uppercase tracking-wider"
                          data-testid="promo-input"
                        />
                        <button
                          onClick={applyPromo}
                          disabled={promoApplying || !promoInput.trim()}
                          className="px-5 h-12 bg-white border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors text-sm font-medium disabled:opacity-60"
                          data-testid="promo-apply-btn"
                        >
                          {promoApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                        </button>
                      </div>
                      {promoError && (
                        <div className="mt-2 text-xs text-[#D92D20]" data-testid="promo-error">{promoError}</div>
                      )}
                    </>
                  )}
                </div>

                <Elements stripe={stripePromise} options={elementsOptions}>
                  <StripePaymentForm totalDisplay={`$${total}`} />
                </Elements>
              </div>
            )}

            {step === 3 && !stripePromise && (
              <div className="bg-white border border-black/10 p-7 text-sm text-[#D92D20]">
                Stripe publishable key not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in frontend/.env.
              </div>
            )}
          </div>

          {/* Summary */}
          <aside className="col-span-12 lg:col-span-5" data-testid="order-summary">
            <div className="lg:sticky lg:top-24 bg-[#0A0A0A] text-[#FFFFFF] p-7">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-6">Order summary</div>
              <div className="flex gap-5 pb-6 border-b border-white/10">
                <img src={BOOK.cover} alt="" className="w-20 h-auto" />
                <div className="flex-1">
                  <div className="font-display font-bold text-xl leading-tight tracking-tight">{BOOK.title}</div>
                  <div className="text-xs text-[#A3A3A3] mt-1">{BOOK.subtitle}</div>
                  <div className="text-xs text-[#A3A3A3] mt-2 font-mono-plex">By {BOOK.author}</div>
                </div>
              </div>

              <div className="py-4 border-b border-white/10 flex items-start gap-3 text-xs" data-testid="signed-reminder">
                <PenLine className="w-4 h-4 text-[#D92D20] mt-0.5 shrink-0" />
                <span className="text-[#A3A3A3]">
                  <span className="text-white font-medium">Signed by Manuel Santana.</span> Direct-order exclusive — not available on Amazon.
                </span>
              </div>

              <div className="py-6 border-b border-white/10">
                <div className="flex items-center justify-between text-sm">
                  <div className="text-[#A3A3A3]">Quantity</div>
                  <div className="inline-flex items-center border border-white/15">
                    <button
                      onClick={() => setQty(Math.max(1, qty - 1))}
                      disabled={step === 3}
                      className="px-3 py-2 hover:bg-white/10 disabled:opacity-40"
                      data-testid="qty-minus"
                    >−</button>
                    <span className="px-4 py-2 font-mono-plex text-sm min-w-[3ch] text-center" data-testid="qty-value">{qty}</span>
                    <button
                      onClick={() => setQty(Math.min(20, qty + 1))}
                      disabled={step === 3}
                      className="px-3 py-2 hover:bg-white/10 disabled:opacity-40"
                      data-testid="qty-plus"
                    >+</button>
                  </div>
                </div>
                {step === 3 && (
                  <div className="text-[10px] text-[#737373] mt-2 uppercase tracking-[0.2em]">
                    Quantity locked during payment
                  </div>
                )}
              </div>

              <dl className="py-6 space-y-3 text-sm border-b border-white/10">
                <div className="flex justify-between"><dt className="text-[#A3A3A3]">Books × {qty}</dt><dd className="font-mono-plex">${bookTotal}</dd></div>
                <div className="flex justify-between"><dt className="text-[#A3A3A3]">Shipping</dt><dd className="font-mono-plex">{selectedRate ? `$${shipCost}` : "—"}</dd></div>
                {applied && (
                  <div className="flex justify-between text-[#D92D20]" data-testid="summary-discount-row">
                    <dt className="font-mono-plex text-xs">PROMO · {applied.code}</dt>
                    <dd className="font-mono-plex">−${discountNum.toFixed(2)}</dd>
                  </div>
                )}
              </dl>
              <div className="pt-6 flex items-end justify-between">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A3A3A3]">Total</div>
                <div className="font-display font-black text-3xl tracking-tighter" data-testid="order-total">${total}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.25em] text-[#525252] font-bold">{label}</label>
      <input
        type={type}
        value={v}
        onChange={onChange}
        className="mt-2 w-full h-12 px-3 bg-white border border-black/15 focus:border-[#0A0A0A] focus:outline-none text-sm"
        data-testid={testid}
      />
    </div>
  );
}
